import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { useCapabilities } from 'hooks/useCapabilities/useCapabilities';
import { useCurrentUser } from 'services/repositories/user/user';
import { DisplayModes } from 'services/webedi/types';
import { useNavigableAnnotations } from 'services/webediErrors/useNavigableAnnotations';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { DocumentType, DocumentTypeWithVirtual, RelationshipV2 } from 'support/types';
import { CreateEditDocumentBottomBanner } from 'pages/Transactions/CreateEditDocumentBottomBanner';
import { Form } from 'components/Form/Form';
import { LoadingOverlay } from 'components/Loading/LoadingOverlay';
import { Page, PAGE_SECTION_PORTAL_ROOT_ID } from 'components/Page/Page';
import { Translate } from 'components/Translate/Translate';
import { DocumentSections } from 'components/WebEDI/DocumentSections/DocumentSections';
import { HiddenErrorsAdminBanner } from 'components/WebEDI/WebEDIMessageErrorBanner/components';
import { WebEDIMessageErrorBanner } from 'components/WebEDI/WebEDIMessageErrorBanner/WebEDIMessageErrorBanner';

import { UploadedFileAlert } from './components/UploadedFileAlert';

export type DocumentCreationStepProps = {
  nonVirtualDocumentType: DocumentType;
  relationship: RelationshipV2 | undefined;
  sourceMessageId: string | undefined;
  handleOpenConfirmation: () => void;
  uploadedFileName: string | undefined;
  onUploadNewFile: () => void;
  isCreatingDocument: boolean;
};

export const DocumentCreationStep = ({
  nonVirtualDocumentType,
  relationship,
  sourceMessageId,
  handleOpenConfirmation,
  uploadedFileName,
  onUploadNewFile,
  isCreatingDocument,
}: DocumentCreationStepProps) => {
  const formMethods = useFormContext();
  const {
    formState: { errors },
  } = formMethods;
  const { data: currentUser } = useCurrentUser();
  const uiConfig = useWebEDIStore((state) => state.uiConfig);
  const hiddenErrors = useWebEDIStore((state) => state.hiddenErrors);
  useCapabilities({ relationshipId: relationship?.id, sourceMessageId, documentType: nonVirtualDocumentType });

  useNavigableAnnotations({
    formMethods,
    uiConfig,
    mode: DisplayModes.create,
    variables: relationship?.receiver_partner ? { receiverPartnerName: relationship.receiver_partner.name } : {},
    documentType: nonVirtualDocumentType as unknown as DocumentTypeWithVirtual,
    senderPartner: relationship?.sender_partner ?? undefined,
    receiverPartner: relationship?.receiver_partner ?? undefined,
    annotations: undefined,
    canonical: undefined,
    flattenData: undefined,
  });

  const errorBannerContent = useMemo(() => {
    if (uploadedFileName) {
      return (
        <div>
          <p className="text-sm font-medium text-gray-900">
            <Translate i18nKey="priceCatalogs:uploaded_error_banner.title" />
          </p>
          <p className="text-sm text-gray-700">
            <Translate i18nKey="priceCatalogs:uploaded_error_banner.description" />
          </p>
        </div>
      );
    }
  }, [uploadedFileName]);
  return (
    <>
      <Page.Section
        topBanner={
          Object.keys(errors).length ? (
            <WebEDIMessageErrorBanner mode={DisplayModes.create} content={errorBannerContent} />
          ) : null
        }
        bottomBanner={<CreateEditDocumentBottomBanner documentType={nonVirtualDocumentType} />}
      >
        <div className="flex flex-col gap-4">
          <UploadedFileAlert uploadedFileName={uploadedFileName} onUploadNewFile={onUploadNewFile} />
          <Form
            noValidate={true}
            onSubmit={handleOpenConfirmation}
            id="webedi-document"
            data-testid="webedi-document"
            autoComplete="off"
          >
            {currentUser?.isAdmin && hiddenErrors.length > 0 ? (
              <HiddenErrorsAdminBanner hiddenErrors={hiddenErrors} />
            ) : null}
            {uiConfig ? (
              <DocumentSections
                mode={DisplayModes.create}
                uiConfig={uiConfig}
                documentType={nonVirtualDocumentType}
                variables={
                  relationship?.receiver_partner ? { receiverPartnerName: relationship.receiver_partner.name } : {}
                }
                isSubmitting={isCreatingDocument}
              />
            ) : null}
          </Form>
        </div>
        <LoadingOverlay isVisible={isCreatingDocument} portalRootId={PAGE_SECTION_PORTAL_ROOT_ID} />
      </Page.Section>
    </>
  );
};
